import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';
import { slugify } from '../utils/cards';
import { useAuth } from '../lib/auth';
import { Link, useSearchParams } from 'react-router-dom';
import EmailTemplates from './admin/EmailTemplates';
import EmailLog from './admin/EmailLog';
import Payments from './admin/Payments';
import Finance from './admin/Finance';
import Invoices from './admin/invoices/Invoices';
import AccountDetail from './admin/AccountDetail';
import { errorBox, inputClass, noticeBox } from './AuthLayout';
import { getPlan, money } from '../config/plans';
import { usePricing } from '../lib/pricing';
import { BRAND } from '../config/brand';

/** Founder offer prices shown in the email: Teams with 25% off. */
function offerPrices() {
  const teams = getPlan('teams');
  return { month: money(teams.price.month * 0.75), year: money((teams.yearTotal ?? teams.price.year * 12) * 0.75) };
}

/** Calls the admin-users Edge Function and surfaces its error message. */
async function adminCall(action, payload = {}) {
  const { data, error } = await supabase.functions.invoke('admin-users', { body: { action, ...payload } });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  return data;
}

const PAGE_SIZE = 25;

const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const SECTIONS = [
  { id: 'companies', label: 'Companies', title: 'Companies', intro: 'Create companies, add people and give free access.' },
  { id: 'users', label: 'Users', title: 'Users', intro: 'Everyone with a Carduzo account.' },
  { id: 'emails', label: 'Email templates', title: 'Email templates', intro: 'Edit the emails Carduzo sends. Changes apply to the next email.' },
  { id: 'log', label: 'Email log', title: 'Email log', intro: 'Every email Carduzo has sent, newest first.' },
  { id: 'invoices', label: 'Invoices', title: 'Invoices', intro: 'Bill businesses for Carduzo or anything else Doggy Canvas LLC sells, track sales tax and record payments.' },
  { id: 'finance', label: 'Finance', title: 'Finance', intro: 'Money in and out: Stripe sales, refunds, fees and payouts, invoice payments, plus your own expenses.' },
  { id: 'payments', label: 'Payments', title: 'Payments', intro: 'Stripe keys and the switch between sandbox and production.' },
];

/** /admin — Carduzo platform admin console. The section lives in ?section= so links and refresh keep it. */
export default function PlatformConsole() {
  const [params, setParams] = useSearchParams();
  const current = SECTIONS.find((x) => x.id === params.get('section')) || SECTIONS[0];
  const go = (id) => setParams(id === 'companies' ? {} : { section: id });
  // ?org=<id> opens one account's details over the Companies or Users list (the list stays mounted
  // underneath, so its search and page survive going back).
  const orgId = ['companies', 'users'].includes(current.id) ? params.get('org') : null;
  const openAccount = (id) => setParams({ section: current.id, org: id });
  const { user, signOut } = useAuth();

  return (
    <div className="min-h-screen bg-surface lg:flex">
      <nav aria-label="Admin" className="bg-navy text-white lg:w-[232px] lg:shrink-0 lg:min-h-screen px-3.5 py-4 lg:py-5 flex lg:flex-col gap-1 items-center lg:items-stretch overflow-x-auto">
        <div className="flex items-center gap-2.5 px-2 lg:pb-5 shrink-0">
          <img src={BRAND.logo.lockupOnDark} alt={BRAND.name} className="h-[28px] w-auto" />
          <span className="h-[22px] px-2 rounded-full bg-coral text-navy text-[11px] font-bold flex items-center">ADMIN</span>
        </div>
        {SECTIONS.map((x) => (
          <button key={x.id} onClick={() => go(x.id)} aria-current={current.id === x.id ? 'page' : undefined}
            className={`h-10 px-3 rounded-lg text-sm text-left shrink-0 ${current.id === x.id ? 'bg-blue text-white font-semibold' : 'text-[#C9D6E6] font-medium hover:bg-white/10 hover:text-white'}`}>
            {x.label}
          </button>
        ))}
        <span className="flex-1" />
        <Link to="/" className="h-10 px-3 rounded-lg text-sm font-medium text-[#C9D6E6] hover:bg-white/10 hover:text-white flex items-center shrink-0">← Back to app</Link>
        <div className="hidden lg:block px-3 pt-3 mt-2 border-t border-white/10 text-xs text-[#C9D6E6] break-all">{user?.email}</div>
        <button onClick={() => signOut()} className="hidden lg:flex h-9 px-3 rounded-lg text-sm text-[#C9D6E6] hover:bg-white/10 hover:text-white items-center">Sign out</button>
      </nav>
      <main className="flex-1 min-w-0 px-4 sm:px-8 py-7 lg:py-8">
        <div className="max-w-[1400px]">
          <h1 className="text-[32px] leading-[38px] font-extrabold tracking-tight">{current.title}</h1>
          <p className="text-ink-muted mt-1 mb-6">{current.intro}</p>
          {orgId && <AccountDetail orgId={orgId} onBack={() => go(current.id)} />}
          <div className={orgId ? 'hidden' : undefined}>
            {current.id === 'companies' && <Companies onOpen={openAccount} />}
            {current.id === 'users' && <Users onOpen={openAccount} />}
          </div>
          {current.id === 'emails' && <EmailTemplates />}
          {current.id === 'log' && <EmailLog />}
          {current.id === 'invoices' && <Invoices />}
          {current.id === 'finance' && <Finance />}
          {current.id === 'payments' && <Payments />}
        </div>
      </main>
    </div>
  );
}

function Companies({ onOpen }) {
  usePricing(); // re-render once the saved prices load (offerPrices reads them)
  const [orgs, setOrgs] = useState(null);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [addingTo, setAddingTo] = useState(null);
  const [freeFor, setFreeFor] = useState(null);
  const [seatsFor, setSeatsFor] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [result, setResult] = useState(null);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  // Wait for a pause in typing before searching; a new search starts at page 1.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('admin_list_organizations', {
      p_search: query, p_status: statusFilter, p_plan: planFilter,
      p_limit: PAGE_SIZE, p_offset: page * PAGE_SIZE,
    });
    if (error) { setError(error.message); return; }
    if (!data.length && page > 0) { setPage((p) => p - 1); return; } // last row on the page went away
    setError('');
    // Same shape the panels (SeatLimit) expect from the old organizations select.
    setOrgs(data.map((o) => ({ ...o, org_members: [{ count: o.member_count }], cards: [{ count: o.card_count }] })));
    setTotal(data[0]?.total_count ?? 0);
  }, [query, statusFilter, planFilter, page]);

  useEffect(() => { load(); }, [load]);

  const filtered = query || statusFilter !== 'all' || planFilter !== 'all';
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const setFilter = (fn) => (e) => { fn(e.target.value); setPage(0); };

  const [offering, setOffering] = useState(null);
  const [offerResult, setOfferResult] = useState(null);
  const sendOffer = async (org) => {
    if (!window.confirm(`Send the founder offer to ${org.name}?\n\nThis gives them 12 months of free access for their admin's card (1 seat) and emails their owners/admins the offer: 25% off Teams for the first year when they add their team.`)) return;
    setOffering(org.id);
    setError('');
    setOfferResult(null);
    try {
      const res = await adminCall('send_offer', { org_id: org.id, offer_prices: offerPrices() });
      setOfferResult({ org: org.name, ...res });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setOffering(null);
    }
  };

  const toggleStatus = async (org) => {
    const status = org.status === 'active' ? 'suspended' : 'active';
    const { error } = await supabase.from('organizations').update({ status }).eq('id', org.id);
    if (error) setError(error.message);
    else load();
  };

  const onDone = (res) => {
    setResult(res);
    setShowCreate(false);
    setAddingTo(null);
    setFreeFor(null);
    setSeatsFor(null);
    setRenaming(null);
    load();
  };

  return (
    <div className="space-y-6">
      {error && <div className={errorBox}>{error}</div>}
      {result && <InviteResult result={result} onClose={() => setResult(null)} />}
      {offerResult && (
        <div className={`${noticeBox} space-y-1`}>
          <p className="font-medium">Founder offer for {offerResult.org}: free until {offerResult.trial_end}.</p>
          {offerResult.results.map((r) => (
            <p key={r.email}>{r.emailed ? '✓ Sent to' : '✗ Not sent to'} {r.email}{r.note && !r.emailed ? ` (${r.note})` : ''}</p>
          ))}
          <button onClick={() => setOfferResult(null)} className="text-sm underline">Dismiss</button>
        </div>
      )}

      {showCreate ? (
        <CreateCompany onDone={onDone} onCancel={() => setShowCreate(false)} />
      ) : (
        <button onClick={() => { setShowCreate(true); setResult(null); }} className="px-4 py-2 rounded-lg bg-blue text-white font-medium hover:bg-blue-dark">
          New company
        </button>
      )}

      {addingTo && <AddPerson org={addingTo} onDone={onDone} onCancel={() => setAddingTo(null)} />}
      {freeFor && <FreeAccess org={freeFor} onDone={() => onDone(null)} onCancel={() => setFreeFor(null)} />}
      {seatsFor && <SeatLimit org={seatsFor} onDone={() => onDone(null)} onCancel={() => setSeatsFor(null)} />}
      {renaming && <RenameAccount org={renaming} onDone={() => onDone(null)} onCancel={() => setRenaming(null)} />}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[240px]">
          <span className="block text-sm font-medium text-ink mb-1">Search</span>
          <input type="search" className={inputClass} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, web address or email" />
        </label>
        <label>
          <span className="block text-sm font-medium text-ink mb-1">Status</span>
          <select className={inputClass} value={statusFilter} onChange={setFilter(setStatusFilter)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </label>
        <label>
          <span className="block text-sm font-medium text-ink mb-1">Plan</span>
          <select className={inputClass} value={planFilter} onChange={setFilter(setPlanFilter)}>
            <option value="all">All</option>
            <option value="free">Free plan</option>
            <option value="paid">Paying</option>
            <option value="complimentary">Free access</option>
          </select>
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-ink-muted">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium text-right">People</th>
              <th className="px-4 py-3 font-medium text-right">Seats</th>
              <th className="px-4 py-3 font-medium text-right">Cards</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {orgs === null && <tr><td colSpan="9" className="px-4 py-6 text-center text-ink-muted">Loading...</td></tr>}
            {orgs?.length === 0 && <tr><td colSpan="9" className="px-4 py-6 text-center text-ink-muted">{filtered ? 'No accounts match.' : 'No accounts yet. Create the first company above.'}</td></tr>}
            {orgs?.map((o) => (
              <tr key={o.id}>
                <td className="px-4 py-3">
                  <button onClick={() => onOpen(o.id)} className="font-medium text-ink hover:underline text-left">{o.name}</button>
                  {o.contact_emails?.map((e) => <div key={e} title={e} className="text-xs text-ink truncate max-w-[240px]">{e}</div>)}
                  <div className="text-xs text-ink-muted">{o.slug}</div>
                </td>
                <td className="px-4 py-3 capitalize">{o.kind}</td>
                <td className="px-4 py-3 text-right tabular-nums">{o.org_members?.[0]?.count ?? 0}</td>
                <td className="px-4 py-3 text-right tabular-nums">{o.kind === 'company' ? (o.seat_limit ?? <span className="text-ink-muted">No limit</span>) : <span className="text-ink-muted">—</span>}</td>
                <td className="px-4 py-3 text-right tabular-nums">{o.cards?.[0]?.count ?? 0}</td>
                <td className="px-4 py-3 whitespace-nowrap"><AccessLabel org={o} /></td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.status === 'active' ? 'bg-blue-soft text-blue' : 'bg-coral-soft text-coral-ink'}`}>{o.status}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-right space-x-3">
                  <button onClick={() => onOpen(o.id)} className="text-ink font-medium hover:underline">Details</button>
                  {o.kind === 'company' && (
                    <button onClick={() => { setAddingTo(o); setResult(null); }} className="text-ink font-medium hover:underline">Add person</button>
                  )}
                  {o.kind === 'company' && (
                    <button onClick={() => { setSeatsFor(o); setResult(null); }} className="text-ink font-medium hover:underline">Seats</button>
                  )}
                  {o.kind === 'company' && (
                    <Link to={`/company/${o.id}`} className="text-ink font-medium hover:underline">Open</Link>
                  )}
                  {o.kind === 'company' && (
                    <button onClick={() => sendOffer(o)} disabled={offering === o.id} className="text-ink font-medium hover:underline disabled:opacity-50">
                      {offering === o.id ? 'Sending…' : 'Send offer'}
                    </button>
                  )}
                  <button onClick={() => { setRenaming(o); setResult(null); }} className="text-ink font-medium hover:underline">Rename</button>
                  <button onClick={() => { setFreeFor(o); setResult(null); }} className="text-ink font-medium hover:underline">Free access</button>
                  <button onClick={() => toggleStatus(o)} className="text-ink-muted hover:underline">
                    {o.status === 'active' ? 'Suspend' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orgs && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-ink-muted">
          <span>{page * PAGE_SIZE + 1}–{page * PAGE_SIZE + orgs.length} of {total}</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page === 0}
              className="h-9 px-3 rounded-lg border border-line-strong bg-white text-ink font-medium hover:bg-surface disabled:opacity-40">Previous</button>
            <span>Page {page + 1} of {pages}</span>
            <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page + 1 >= pages}
              className="h-9 px-3 rounded-lg border border-line-strong bg-white text-ink font-medium hover:bg-surface disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-line p-6">
      <h2 className="font-semibold text-ink mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-ink-muted mt-1">{hint}</span>}
    </label>
  );
}

function CreateCompany({ onDone, onCancel }) {
  usePricing(); // re-render once the saved prices load (offerPrices reads them)
  const [form, setForm] = useState({ name: '', slug: '', admin_name: '', admin_email: '', create_card: true, email_template: 'founder_offer' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      onDone(await adminCall('create_company', { ...form, slug: form.slug || slugify(form.name), offer_prices: offerPrices() }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="New company">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <Field label="Company name">
          <input className={inputClass} value={form.name} required disabled={saving}
            onChange={(e) => { set('name')(e); if (!slugTouched) setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }} />
        </Field>
        <Field label="Short name" hint="Lowercase letters, numbers and dashes. Used in links.">
          <input className={inputClass} value={form.slug} required disabled={saving}
            onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() })); }} />
        </Field>
        <Field label="Company admin's name">
          <input className={inputClass} value={form.admin_name} onChange={set('admin_name')} disabled={saving} />
        </Field>
        <Field label="Company admin's email" hint="They get an email with a link that signs them in.">
          <input type="email" className={inputClass} value={form.admin_email} onChange={set('admin_email')} required disabled={saving} />
        </Field>
        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={form.create_card} onChange={set('create_card')} disabled={saving} />
          Create a card for the admin too
        </label>
        <fieldset className="sm:col-span-2">
          <legend className="block text-sm font-medium text-ink mb-2">Email and offer</legend>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ['founder_offer', 'Founder offer', `12 months free for the admin's own card plus a hand-delivered NFC card, then 25% off Teams for the first year when they add their team (${offerPrices().month}/person/mo or ${offerPrices().year}/person/yr; yearly: $10 per extra card, free for 10+). Sends the “Founder offer” email from you.`],
              ['standard', 'Standard invitation', 'Just the invitation to set up their card. The company stays on the Free plan unless you give free access.'],
            ].map(([id, label, text]) => (
              <label key={id} className={`p-3 rounded-xl cursor-pointer ${form.email_template === id ? 'border-2 border-blue bg-blue-soft' : 'border border-line bg-white'}`}>
                <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <input type="radio" name="email_template" value={id} checked={form.email_template === id} onChange={set('email_template')} disabled={saving} />
                  {label}
                </span>
                <span className="block text-sm text-ink-muted mt-1">{text}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {error && <div className={`${errorBox} sm:col-span-2`}>{error}</div>}
        <div className="sm:col-span-2 flex gap-3">
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-blue text-white font-medium disabled:opacity-50">
            {saving ? 'Creating...' : 'Create company and invite admin'}
          </button>
          <button type="button" onClick={onCancel} className="px-5 py-2 rounded-lg border border-line-strong font-medium">Cancel</button>
        </div>
      </form>
    </Panel>
  );
}

function AddPerson({ org, onDone, onCancel }) {
  const [form, setForm] = useState({ full_name: '', email: '', role: 'member', create_card: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      onDone(await adminCall('invite_member', { ...form, org_id: org.id }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title={`Add a person to ${org.name}`}>
      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-4">
        <Field label="Name"><input className={inputClass} value={form.full_name} onChange={set('full_name')} disabled={saving} /></Field>
        <Field label="Email"><input type="email" className={inputClass} value={form.email} onChange={set('email')} required disabled={saving} /></Field>
        <Field label="Role">
          <select className={inputClass} value={form.role} onChange={set('role')} disabled={saving}>
            <option value="member">Employee</option>
            <option value="admin">Company admin</option>
          </select>
        </Field>
        <label className="sm:col-span-3 flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={form.create_card} onChange={set('create_card')} disabled={saving} />
          Create their card now
        </label>
        {error && <div className={`${errorBox} sm:col-span-3`}>{error}</div>}
        <div className="sm:col-span-3 flex gap-3">
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-blue text-white font-medium disabled:opacity-50">
            {saving ? 'Sending...' : 'Send invitation'}
          </button>
          <button type="button" onClick={onCancel} className="px-5 py-2 rounded-lg border border-line-strong font-medium">Cancel</button>
        </div>
      </form>
    </Panel>
  );
}

function InviteResult({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(result.invite_link); setCopied(true); } catch { window.prompt('Invite link:', result.invite_link); }
  };
  return (
    <div className={`${result.emailed ? noticeBox : 'bg-coral-soft border border-coral/40 text-ink px-4 py-3 rounded-lg text-sm'} space-y-2`}>
      <p className="font-medium">
        {result.organization ? `Created ${result.organization.name}. ` : ''}
        {result.emailed ? (result.email_note || 'Invitation email sent.') : result.email_note}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="bg-white/70 px-2 py-1 rounded break-all">{result.invite_link}</code>
        <button onClick={copy} className="px-3 py-1 rounded border border-current">{copied ? 'Copied' : 'Copy link'}</button>
        <button onClick={onClose} className="px-3 py-1">Dismiss</button>
      </div>
      {result.card_slug && <p>Card link: <a className="underline" href={`/c/${result.card_slug}`} target="_blank" rel="noreferrer">/c/{result.card_slug}</a></p>}
    </div>
  );
}

function Users({ onOpen }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // list_users has no account ids, so read the memberships (platform admins see them all) to
    // make each account a link to its details.
    Promise.all([
      adminCall('list_users'),
      supabase.from('org_members').select('user_id, org_id, role, status, organizations(name, kind)'),
    ]).then(([d, { data: members, error }]) => {
      if (error) throw error;
      setUsers(d.users.map((u) => ({
        ...u,
        memberships: members.filter((m) => m.user_id === u.id).map((m) => ({
          org_id: m.org_id, role: m.role, status: m.status, org_name: m.organizations?.name, org_kind: m.organizations?.kind,
        })),
      })));
    }).catch((err) => setError(err.message));
  }, []);

  return (
    <div className="space-y-4">
      {error && <div className={errorBox}>{error}</div>}
      <div className="bg-white rounded-2xl border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-ink-muted">
            <tr>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Belongs to</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium">Last sign-in</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {users === null && !error && <tr><td colSpan="4" className="px-4 py-6 text-center text-ink-muted">Loading...</td></tr>}
            {users?.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3">
                  <div className="font-medium text-ink">{u.email}</div>
                  <div className="text-xs text-ink-muted">
                    {u.platform_admin && <span className="mr-2 text-blue font-semibold">Platform admin</span>}
                    {!u.confirmed && 'Invitation not accepted yet'}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {u.memberships.length === 0 ? <span className="text-ink-muted">—</span> : u.memberships.map((m, i) => (
                    <div key={i}>
                      <button onClick={() => onOpen(m.org_id)} className="text-ink hover:underline text-left">{m.org_kind === 'personal' ? 'Personal account' : m.org_name}</button>
                      {' '}<span className="text-ink-muted capitalize">· {m.role}</span>
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(u.created_at)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(u.last_sign_in_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PLAN_NAMES = { free: 'Free', pro: 'Pro', teams: 'Teams', enterprise: 'Enterprise' };

function AccessLabel({ org }) {
  if (org.billing_status === 'complimentary') {
    const expired = org.comp_until && new Date(org.comp_until) < new Date();
    return (
      <div title={org.comp_note || ''}>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${expired ? 'bg-surface text-ink-muted' : 'bg-blue-100 text-blue-800'}`}>
          {expired ? 'Free access ended' : `Free · ${PLAN_NAMES[org.plan] || 'Pro'}`}
        </span>
        <div className="text-xs text-ink-muted mt-1">{org.comp_until ? `${expired ? 'Ended' : 'Until'} ${formatDate(org.comp_until)}` : 'No end date'}</div>
      </div>
    );
  }
  if (org.billing_status === 'none') return <span className="text-ink-muted">Free plan</span>;
  if (org.billing_status === 'canceled') return <div><span className="text-ink-muted">Free plan</span><div className="text-xs text-ink-muted mt-1">Subscription canceled</div></div>;
  return <span className="capitalize">{PLAN_NAMES[org.plan] || ''} {org.billing_status.replace('_', ' ')}</span>;
}

const addMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/** Give an account free (complimentary) access to a paid plan, optionally until a date. */
function FreeAccess({ org, onDone, onCancel }) {
  const { user } = useAuth();
  const isComp = org.billing_status === 'complimentary';
  const [plan, setPlan] = useState(isComp && org.plan ? org.plan : org.kind === 'company' ? 'teams' : 'pro');
  const [length, setLength] = useState(isComp ? (org.comp_until ? 'date' : 'forever') : '12');
  const [until, setUntil] = useState(isComp && org.comp_until ? org.comp_until.slice(0, 10) : addMonths(12));
  const [note, setNote] = useState(isComp ? org.comp_note || '' : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const endDate = length === 'forever' ? null : length === 'date' ? until : addMonths(Number(length));

  const save = async (e) => {
    e.preventDefault();
    setError('');
    if (endDate && new Date(endDate) <= new Date()) return setError('Choose an end date in the future.');
    setSaving(true);
    const { error } = await supabase.from('organizations').update({
      billing_status: 'complimentary',
      plan,
      comp_until: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null,
      comp_note: note.trim() || null,
      comp_granted_by: user.id,
      comp_granted_at: new Date().toISOString(),
    }).eq('id', org.id);
    setSaving(false);
    if (error) setError(error.message);
    else onDone();
  };

  const end = async () => {
    setSaving(true);
    const { error } = await supabase.from('organizations').update({
      billing_status: 'none', plan: null, comp_until: null, comp_note: null, comp_granted_by: null, comp_granted_at: null,
    }).eq('id', org.id);
    setSaving(false);
    if (error) setError(error.message);
    else onDone();
  };

  return (
    <Panel title={`Free access for ${org.kind === 'personal' ? 'this personal account' : org.name}`}>
      <form onSubmit={save} className="grid sm:grid-cols-3 gap-4">
        <Field label="Plan">
          <select className={inputClass} value={plan} onChange={(e) => setPlan(e.target.value)} disabled={saving}>
            <option value="pro">Pro</option>
            <option value="teams">Teams</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </Field>
        <Field label="For how long">
          <select className={inputClass} value={length} onChange={(e) => setLength(e.target.value)} disabled={saving}>
            <option value="1">1 month</option>
            <option value="3">3 months</option>
            <option value="6">6 months</option>
            <option value="12">12 months</option>
            <option value="date">Until a date</option>
            <option value="forever">No end date</option>
          </select>
        </Field>
        {length === 'date' ? (
          <Field label="End date">
            <input type="date" className={inputClass} value={until} min={addMonths(0)} onChange={(e) => setUntil(e.target.value)} disabled={saving} required />
          </Field>
        ) : (
          <div className="text-sm text-ink-muted self-end pb-2">{endDate ? `Ends ${formatDate(endDate)}` : 'Stays free until you end it'}</div>
        )}
        <div className="sm:col-span-3">
          <Field label="Reason (only admins see this)">
            <input className={inputClass} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Pilot customer, partner, friends and family" disabled={saving} />
          </Field>
        </div>
        {error && <div className={`${errorBox} sm:col-span-3`}>{error}</div>}
        <div className="sm:col-span-3 flex flex-wrap gap-3">
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-blue text-white font-medium disabled:opacity-50">
            {saving ? 'Saving...' : isComp ? 'Update free access' : 'Give free access'}
          </button>
          {isComp && <button type="button" onClick={end} disabled={saving} className="px-5 py-2 rounded-lg border border-coral-ink text-coral-ink font-medium">End free access</button>}
          <button type="button" onClick={onCancel} className="px-5 py-2 rounded-lg border border-line-strong font-medium">Cancel</button>
        </div>
      </form>
    </Panel>
  );
}

/** Set how many people a company can have (active members + pending invites). Blank = no limit. */
/** Rename an account (company or personal): name, short name, and optionally the company line on its cards. */
function RenameAccount({ org, onDone, onCancel }) {
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [cards, setCards] = useState(null); // cards whose company line still shows the old name
  const [updateCards, setUpdateCards] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('cards').select('id', { count: 'exact', head: true }).eq('org_id', org.id).eq('company', org.name)
      .then(({ count }) => setCards(count ?? 0));
  }, [org.id, org.name]);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const newName = name.trim();
    const newSlug = slugify(slug || newName);
    if (!newName) return setError('Enter a name.');
    setSaving(true);
    const { error: orgErr } = await supabase.from('organizations').update({ name: newName, slug: newSlug }).eq('id', org.id);
    if (orgErr) {
      setSaving(false);
      return setError(orgErr.code === '23505' ? `The short name "${newSlug}" is taken. Choose another.` : orgErr.message);
    }
    if (updateCards && cards && newName !== org.name) {
      const { error: cardErr } = await supabase.from('cards').update({ company: newName }).eq('org_id', org.id).eq('company', org.name);
      if (cardErr) {
        setSaving(false);
        return setError(`Renamed, but the cards could not be updated: ${cardErr.message}`);
      }
    }
    setSaving(false);
    onDone();
  };

  return (
    <Panel title={`Rename ${org.name}`}>
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
        <Field label={org.kind === 'company' ? 'Company name' : 'Account name'}>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required disabled={saving} />
        </Field>
        <Field label="Short name" hint="Lowercase letters, numbers and dashes. Only shown in the admin; card links don't change.">
          <input className={inputClass} value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} required disabled={saving} />
        </Field>
        {cards > 0 && (
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={updateCards} onChange={(e) => setUpdateCards(e.target.checked)} disabled={saving} />
            Also change the company line on {cards} {cards === 1 ? 'card that shows' : 'cards that show'} “{org.name}”
          </label>
        )}
        {error && <div className={`${errorBox} sm:col-span-2`}>{error}</div>}
        <div className="sm:col-span-2 flex flex-wrap gap-3">
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-blue text-white font-medium disabled:opacity-50">
            {saving ? 'Saving...' : 'Save name'}
          </button>
          <button type="button" onClick={onCancel} className="px-5 py-2 rounded-lg border border-line-strong font-medium">Cancel</button>
        </div>
      </form>
    </Panel>
  );
}

function SeatLimit({ org, onDone, onCancel }) {
  const [value, setValue] = useState(org.seat_limit == null ? '' : String(org.seat_limit));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const people = org.org_members?.[0]?.count ?? 0;

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const n = value.trim() === '' ? null : Number(value);
    if (n !== null && (!Number.isInteger(n) || n < 1)) return setError('Enter a whole number of seats (1 or more), or leave it blank for no limit.');
    setSaving(true);
    const { error } = await supabase.from('organizations').update({ seat_limit: n }).eq('id', org.id);
    setSaving(false);
    if (error) setError(error.message);
    else onDone();
  };

  return (
    <Panel title={`Seats for ${org.name}`}>
      <form onSubmit={save} className="grid sm:grid-cols-3 gap-4 items-end">
        <Field label="Seat limit" hint="Members plus pending invites. Leave blank for no limit.">
          <input type="number" min="1" step="1" inputMode="numeric" className={inputClass} value={value}
            onChange={(e) => setValue(e.target.value)} placeholder="No limit" disabled={saving} />
        </Field>
        <p className="text-sm text-ink-muted sm:col-span-2 pb-6">
          {people} {people === 1 ? 'person has' : 'people have'} joined. Lowering the limit below what's in use keeps everyone, but blocks new invites.
        </p>
        {error && <div className={`${errorBox} sm:col-span-3`}>{error}</div>}
        <div className="sm:col-span-3 flex flex-wrap gap-3">
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-blue text-white font-medium disabled:opacity-50">
            {saving ? 'Saving...' : 'Save seat limit'}
          </button>
          <button type="button" onClick={onCancel} className="px-5 py-2 rounded-lg border border-line-strong font-medium">Cancel</button>
        </div>
      </form>
    </Panel>
  );
}
